/**
 * BookingFlow — /booking
 *
 * 3-step multi-program booking form.
 * Step 1: Select programs / trip
 * Step 2: Participant details (parent + ward if minor)
 * Step 3: Review & confirm
 *
 * Existing /rajdharma/dhanurveda/booking remains fully functional.
 */
import { useState } from 'react';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/BookingFlow.css';

interface Props {
  onNavigate: (path: string) => void;
}

type Step = 1 | 2 | 3 | 4;

interface BookingData {
  programs: string[];
  tripId: string;
  name: string;
  email: string;
  phone: string;
  pincode: string;
  age: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  message: string;
}

const EMPTY: BookingData = {
  programs: [], tripId: '',
  name: '', email: '', phone: '', pincode: '', age: '',
  guardianName: '', guardianEmail: '', guardianPhone: '',
  message: '',
};

const PROGRAMS = [
  { id: 'dhanurveda', icon: '🏹', hi: 'धनुर्वेद — पारम्परिक काष्ठ-धनुष अभ्यास', en: 'Dhanurveda — Traditional wooden-bow archery' },
  { id: 'samvad',     icon: '🗣️', hi: 'संस्कृत संवाद — युवा संवाद कार्यक्रम',  en: 'Sanskrit Samvad — Youth conversation program' },
  { id: 'trip',       icon: '🏕️', hi: 'शैक्षिक यात्रा — धर्मशाला, हि.प्र.',   en: 'Educational Trip — Dharamshala, H.P.' },
];

export default function BookingFlow({ onNavigate }: Props) {
  const { t, locale } = useGlossary();
  const hi = locale === 'hi';
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<BookingData>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [refId, setRefId] = useState('');

  const tl = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  const isMinor = Number(data.age) > 0 && Number(data.age) < 18;

  const toggleProgram = (id: string) => {
    setData(prev => ({
      ...prev,
      programs: prev.programs.includes(id)
        ? prev.programs.filter(p => p !== id)
        : [...prev.programs, id],
    }));
  };

  const step1Valid = data.programs.length > 0;
  const step2Valid = data.name.trim() && data.email.trim() && data.age.trim() &&
    (!isMinor || (data.guardianName.trim() && data.guardianEmail.trim()));

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch('/api/booking/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const d = await res.json();
      if (res.ok) {
        setRefId(d.ref_id ?? 'BK-' + Date.now());
        setStep(4);
      } else {
        setError(d.error ?? (hi ? 'त्रुटि हुई। पुनः प्रयास करें।' : 'Error. Please try again.'));
      }
    } catch {
      setError(hi ? 'नेटवर्क त्रुटि' : 'Network error');
    }
    setSubmitting(false);
  };

  const PROGRAM_LABELS: Record<string, string> = {
    dhanurveda: hi ? 'धनुर्वेद 🏹' : 'Dhanurveda 🏹',
    samvad:     hi ? 'संस्कृत संवाद 🗣️' : 'Sanskrit Samvad 🗣️',
    trip:       hi ? 'शैक्षिक यात्रा 🏕️' : 'Educational Trip 🏕️',
  };

  return (
    <div className="bf-page">
      {/* Header */}
      <div className="bf-hero">
        <span className="brand-section-label">{tl('booking.label', 'Booking')}</span>
        <h1 className="bf-hero-title">
          {hi ? 'राजधर्म कार्यक्रम — बुकिंग' : 'Rajadharma Programs — Booking'}
        </h1>
        <p className="bf-hero-sub">
          {hi
            ? 'धनुर्वेद, संस्कृत संवाद या शैक्षिक यात्रा — एक सुरक्षित तीन-चरण प्रक्रिया।'
            : 'Dhanurveda, Sanskrit Samvad, or Educational Trip — a secure three-step process.'}
        </p>
      </div>

      <div className="bf-container">
        {/* Stepper (only for steps 1–3) */}
        {step < 4 && (
          <div className="bf-stepper">
            {[
              { n: 1, hi: 'कार्यक्रम',   en: 'Program' },
              { n: 2, hi: 'विवरण',       en: 'Details' },
              { n: 3, hi: 'पुष्टि करें', en: 'Confirm' },
            ].map(s => (
              <div key={s.n} className={`bf-step ${step >= s.n ? 'bf-step--done' : ''} ${step === s.n ? 'bf-step--active' : ''}`}>
                <div className="bf-step-circle">{step > s.n ? '✓' : s.n}</div>
                <span className="bf-step-label">{hi ? s.hi : s.en}</span>
              </div>
            ))}
          </div>
        )}

        {/* Step 1: Program selection */}
        {step === 1 && (
          <div className="bf-step-body">
            <h2 className="bf-step-title">
              {hi ? 'कौन-से कार्यक्रम में रुचि है?' : 'Which programs are you interested in?'}
            </h2>
            <div className="bf-program-list">
              {PROGRAMS.map(p => (
                <label key={p.id} className={`bf-program-option ${data.programs.includes(p.id) ? 'bf-program-option--checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={data.programs.includes(p.id)}
                    onChange={() => toggleProgram(p.id)}
                  />
                  <span className="bf-prog-icon">{p.icon}</span>
                  <span>{hi ? p.hi : p.en}</span>
                </label>
              ))}
            </div>
            <button
              className="bf-btn-primary"
              disabled={!step1Valid}
              onClick={() => setStep(2)}
            >
              {hi ? 'आगे →' : 'Next →'}
            </button>
          </div>
        )}

        {/* Step 2: Participant details */}
        {step === 2 && (
          <div className="bf-step-body">
            <h2 className="bf-step-title">
              {hi ? 'प्रतिभागी विवरण' : 'Participant Details'}
            </h2>
            <div className="bf-form-grid">
              <div className="bf-field">
                <label>{hi ? 'पूरा नाम *' : 'Full name *'}</label>
                <input value={data.name} onChange={e => setData(p => ({...p, name: e.target.value}))} placeholder={hi ? 'नाम' : 'Name'} />
              </div>
              <div className="bf-field">
                <label>{hi ? 'ईमेल *' : 'Email *'}</label>
                <input type="email" value={data.email} onChange={e => setData(p => ({...p, email: e.target.value}))} placeholder="email@example.com" />
              </div>
              <div className="bf-field">
                <label>{hi ? 'फ़ोन' : 'Phone'}</label>
                <input type="tel" value={data.phone} onChange={e => setData(p => ({...p, phone: e.target.value}))} placeholder="+91 98765 43210" />
              </div>
              <div className="bf-field">
                <label>{hi ? 'पिनकोड' : 'Pincode'}</label>
                <input value={data.pincode} onChange={e => setData(p => ({...p, pincode: e.target.value}))} placeholder="110001" maxLength={6} />
              </div>
              <div className="bf-field">
                <label>{hi ? 'आयु *' : 'Age *'}</label>
                <input type="number" min={1} max={80} value={data.age} onChange={e => setData(p => ({...p, age: e.target.value}))} placeholder="18" />
              </div>
            </div>

            {/* Guardian fields for minors */}
            {isMinor && (
              <div className="bf-guardian-section">
                <p className="bf-guardian-notice">
                  ⚠️ {hi
                    ? 'नाबालिग प्रतिभागी — अभिभावक विवरण आवश्यक'
                    : 'Minor participant — guardian details required'}
                </p>
                <div className="bf-form-grid">
                  <div className="bf-field">
                    <label>{hi ? 'अभिभावक का नाम *' : 'Guardian name *'}</label>
                    <input value={data.guardianName} onChange={e => setData(p => ({...p, guardianName: e.target.value}))} placeholder={hi ? 'अभिभावक नाम' : 'Guardian name'} />
                  </div>
                  <div className="bf-field">
                    <label>{hi ? 'अभिभावक ईमेल *' : 'Guardian email *'}</label>
                    <input type="email" value={data.guardianEmail} onChange={e => setData(p => ({...p, guardianEmail: e.target.value}))} placeholder="guardian@example.com" />
                  </div>
                  <div className="bf-field">
                    <label>{hi ? 'अभिभावक फ़ोन' : 'Guardian phone'}</label>
                    <input type="tel" value={data.guardianPhone} onChange={e => setData(p => ({...p, guardianPhone: e.target.value}))} placeholder="+91" />
                  </div>
                </div>
              </div>
            )}

            <div className="bf-field bf-field--full">
              <label>{hi ? 'संदेश (वैकल्पिक)' : 'Message (optional)'}</label>
              <textarea rows={3} value={data.message} onChange={e => setData(p => ({...p, message: e.target.value}))} placeholder={hi ? 'कोई विशेष जानकारी या प्रश्न?' : 'Any special requirements or questions?'} />
            </div>

            <div className="bf-nav-row">
              <button className="bf-btn-ghost" onClick={() => setStep(1)}>← {hi ? 'वापस' : 'Back'}</button>
              <button className="bf-btn-primary" disabled={!step2Valid} onClick={() => setStep(3)}>
                {hi ? 'समीक्षा करें →' : 'Review →'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & confirm */}
        {step === 3 && (
          <div className="bf-step-body">
            <h2 className="bf-step-title">{hi ? 'बुकिंग की पुष्टि करें' : 'Confirm your booking'}</h2>
            <div className="bf-review-card">
              <div className="bf-review-row">
                <span>{hi ? 'चुने गए कार्यक्रम' : 'Selected programs'}</span>
                <span>{data.programs.map(p => PROGRAM_LABELS[p]).join(', ')}</span>
              </div>
              <div className="bf-review-row">
                <span>{hi ? 'नाम' : 'Name'}</span><span>{data.name}</span>
              </div>
              <div className="bf-review-row">
                <span>{hi ? 'ईमेल' : 'Email'}</span><span>{data.email}</span>
              </div>
              <div className="bf-review-row">
                <span>{hi ? 'आयु' : 'Age'}</span><span>{data.age}</span>
              </div>
              {isMinor && <div className="bf-review-row">
                <span>{hi ? 'अभिभावक' : 'Guardian'}</span><span>{data.guardianName} ({data.guardianEmail})</span>
              </div>}
              {data.message && <div className="bf-review-row">
                <span>{hi ? 'संदेश' : 'Message'}</span><span>{data.message}</span>
              </div>}
            </div>
            {error && <p className="bf-error">{error}</p>}
            <div className="bf-nav-row">
              <button className="bf-btn-ghost" onClick={() => setStep(2)}>← {hi ? 'वापस' : 'Back'}</button>
              <button className="bf-btn-primary" disabled={submitting} onClick={submit}>
                {submitting ? (hi ? 'भेज रहे हैं...' : 'Submitting...') : (hi ? '✅ बुकिंग करें' : '✅ Confirm Booking')}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="bf-step-body bf-success">
            <div className="bf-success-icon">🎉</div>
            <h2>{hi ? 'बुकिंग सफल!' : 'Booking Received!'}</h2>
            <p>
              {hi
                ? `आपकी बुकिंग संदर्भ संख्या है: ${refId}। हमारी टीम शीघ्र ही आपसे संपर्क करेगी।`
                : `Your booking reference is: ${refId}. Our team will contact you shortly.`}
            </p>
            <div className="bf-success-actions">
              <button className="bf-btn-primary" onClick={() => onNavigate('/')}>
                {hi ? '🏡 होम पर जाएँ' : '🏡 Go Home'}
              </button>
              <button className="bf-btn-ghost" onClick={() => onNavigate('/impact')}>
                {hi ? '📊 प्रभाव देखें' : '📊 View Impact'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
